import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Clock, Crown, Fire, MapPin, Storefront } from "@phosphor-icons/react";
import { motion } from "framer-motion";
import { API, mediaUrl } from "../lib/api";
import { usePublicLocation } from "../hooks/usePublicCity";

const PULSE_STYLES = {
  "Packed": "text-neon-green border-neon-green/40 bg-neon-green/10",
  "Heating Up": "text-neon-gold border-neon-gold/40 bg-neon-gold/10",
  "Building Up": "text-neon-violet border-neon-violet/40 bg-neon-violet/10",
  "Chill": "text-zinc-400 border-white/15 bg-white/5",
};

export default function VenueDirectory() {
  const [venues, setVenues] = useState(null);
  const [founders, setFounders] = useState([]);
  const [city, setCity] = useState("all");
  const [showAll, setShowAll] = useState(false);
  const location = usePublicLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (!location.resolved) return;
    const params = {};
    if (!showAll && location.has_location) {
      params.city = location.city;
      if (location.lat !== null && location.lng !== null) {
        params.lat = location.lat;
        params.lng = location.lng;
        params.max_km = 50;
      }
    }
    API.get("/venues", { params }).then((r) => setVenues(r.data)).catch(() => setVenues([]));
  }, [location.resolved, location.has_location, location.city, location.lat, location.lng, showAll]);

  useEffect(() => {
    if (!location.resolved) return;
    const params = {};
    if (!showAll && location.has_location) params.city = location.city;
    API.get("/venues/founders", { params }).then((r) => setFounders(r.data)).catch(() => setFounders([]));
  }, [location.resolved, location.has_location, location.city, showAll]);

  const cities = [...new Set((venues || []).map((v) => v.city).filter(Boolean))].sort();
  const shown = city === "all" ? (venues || []) : (venues || []).filter((v) => v.city === city);

  return (
    <div data-testid="venue-directory" className="min-h-screen bg-[#050505] text-white">
      <header className="glass sticky top-0 z-40" style={{ paddingTop: "env(safe-area-inset-top)" }}>
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
          <Link to="/" data-testid="directory-logo" className="flex items-center gap-3">
            <ArrowLeft size={16} className="text-zinc-500" />
            <img src="/logo.png" alt="NITEPASS" className="h-12 w-auto" />
          </Link>
          <Link to="/auth?mode=register" data-testid="directory-get-pass"
            className="btn-neon !px-5 !py-2 text-sm">Get the Pass</Link>
        </div>
      </header>

      <div className="mx-auto max-w-6xl px-6 py-12">
        <div className="neon-overline !text-neon-gold" data-testid="directory-city-label">{location.city.toUpperCase()} · ON NITEPASS</div>
        <h1 className="mt-3 font-display text-4xl font-black tracking-tight sm:text-5xl">THE VENUES</h1>
        <p className="mt-3 max-w-xl text-sm leading-relaxed text-zinc-400">
          Every spot on the map — live pulse, tonight's events, verified crowds.
          Scan in with your NITEPASS at the door.
        </p>

        {location.has_location && (
          <div className="mt-5 flex flex-wrap gap-2" data-testid="venue-location-scope">
            <button data-testid="venues-nearby-toggle" onClick={() => { setShowAll(false); setCity("all"); }}
              className={`rounded-full border px-4 py-1.5 text-[11px] font-black uppercase tracking-wider transition-colors duration-200 ${!showAll ? "border-neon-gold/60 bg-neon-gold/15 text-neon-gold" : "border-white/10 text-zinc-500 hover:text-white"}`}>
              Near {location.city}
            </button>
            <button data-testid="venues-all-cities-toggle" onClick={() => { setShowAll(true); setCity("all"); }}
              className={`rounded-full border px-4 py-1.5 text-[11px] font-black uppercase tracking-wider transition-colors duration-200 ${showAll ? "border-neon-gold/60 bg-neon-gold/15 text-neon-gold" : "border-white/10 text-zinc-500 hover:text-white"}`}>
              All Cities
            </button>
          </div>
        )}

        {venues !== null && showAll && cities.length > 1 && (
          <div className="mt-5 flex flex-wrap gap-2" data-testid="city-filter">
            {["all", ...cities].map((c) => (
              <button key={c} data-testid={`city-pill-${c}`} onClick={() => setCity(c)}
                className={`rounded-full border px-4 py-1.5 text-[11px] font-black uppercase tracking-wider transition-colors duration-200 ${city === c ? "border-neon-gold/60 bg-neon-gold/15 text-neon-gold" : "border-white/10 text-zinc-500 hover:text-white"}`}>
                {c === "all" ? "All Cities" : c}
              </button>
            ))}
          </div>
        )}

        {location.resolved && founders.length > 0 && (
          <div className="mt-8" data-testid="founder-strip">
            <div className="flex items-center gap-2 text-[11px] font-black uppercase tracking-widest text-neon-gold">
              <Crown size={14} weight="fill" />
              Founding Venues {!showAll && location.has_location ? `of ${location.city}` : "Worldwide"}
            </div>
            <div className="mt-3 flex gap-3 overflow-x-auto pb-2">
              {founders.map((f) => (
                <button key={f.id} data-testid={`founder-badge-${f.id}`} onClick={() => navigate(`/venue/${f.id}`)}
                  className="flex shrink-0 items-center gap-3 rounded-2xl border border-neon-gold/40 bg-neon-gold/5 px-4 py-3 text-left shadow-glow-gold transition-transform duration-200 hover:-translate-y-0.5">
                  {f.image ? (
                    <img src={mediaUrl(f.image)} alt={f.name} className="h-10 w-10 rounded-xl object-cover" />
                  ) : (
                    <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-neon-gold/15 font-display text-lg font-black text-neon-gold">
                      {f.name[0]?.toUpperCase()}
                    </span>
                  )}
                  <span>
                    <span className="block font-display text-sm font-black">{f.name}</span>
                    <span className="block text-[10px] font-bold uppercase tracking-wider text-neon-gold">
                      Founder{f.city ? ` · ${f.city}` : ""}
                    </span>
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}

        {venues === null ? (
          <p className="py-20 text-center text-sm text-zinc-600">Loading venues…</p>
        ) : venues.length === 0 ? (          <div className="mt-10 rounded-3xl border border-neon-gold/25 bg-neon-gold/5 p-10 text-center" data-testid="directory-empty">
            <Storefront size={36} className="mx-auto text-neon-gold" />
            <p className="mt-4 font-display text-xl font-black">
              {location.has_location && !showAll ? `No venues near ${location.city} yet` : "The first venues are joining now"}
            </p>
            {location.has_location && !showAll ? (
              <>
                <p className="mx-auto mt-2 max-w-sm text-sm text-zinc-500">
                  No partner venues are live within 50 km yet. Browse every city instead.
                </p>
                <button data-testid="venues-show-all-empty" onClick={() => setShowAll(true)}
                  className="btn-neon mt-6 inline-flex !px-6 !py-2.5 text-sm">View All Cities</button>
              </>
            ) : (
              <>
                <p className="mx-auto mt-2 max-w-sm text-sm text-zinc-500">
                  Own a venue? Founding venues lock in a lifetime rate — spots are limited.
                </p>
                <Link to="/auth?mode=register" data-testid="directory-empty-cta"
                  className="btn-neon mt-6 inline-flex !px-6 !py-2.5 text-sm">Claim a Founders Spot</Link>
              </>
            )}
          </div>
        ) : (
          <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="directory-grid">
            {shown.map((v, i) => (
              <motion.button key={v.id} data-testid={`venue-card-${v.id}`}
                initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.06, duration: 0.4 }}
                onClick={() => navigate(`/venue/${v.id}`)}
                className="group overflow-hidden rounded-3xl border border-white/10 bg-[#0a0a0a] text-left transition-all duration-300 hover:-translate-y-1 hover:border-neon-gold/40 hover:shadow-glow-gold">
                <div className="relative h-44 overflow-hidden bg-zinc-900">
                  {v.image ? (
                    <img src={mediaUrl(v.image)} alt={v.name} loading="lazy"
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  ) : (
                    <div className="flex h-full items-center justify-center">
                      <Storefront size={40} className="text-zinc-700" />
                    </div>
                  )}
                  <span className={`absolute right-3 top-3 rounded-full border px-2.5 py-1 text-[10px] font-black uppercase tracking-wider backdrop-blur ${PULSE_STYLES[v.pulse_label] || PULSE_STYLES.Chill}`}
                    data-testid={`venue-pulse-${v.id}`}>
                    {v.pulse_label} · {v.pulse}
                  </span>
                  {v.founder && (
                    <span data-testid={`venue-founder-${v.id}`}
                      className="absolute left-3 top-3 flex items-center gap-1 rounded-full border border-neon-gold/60 bg-black/70 px-2.5 py-1 text-[10px] font-black uppercase tracking-wider text-neon-gold backdrop-blur">
                      <Crown size={11} weight="fill" /> Founder
                    </span>
                  )}
                </div>
                <div className="p-5">
                  <div className="font-display text-lg font-black">{v.name}</div>
                  <div className="mt-1.5 space-y-1 text-xs text-zinc-500">
                    {v.address && <div className="flex items-center gap-1.5"><MapPin size={12} /> {v.address}</div>}
                    {v.open_until && <div className="flex items-center gap-1.5"><Clock size={12} /> Open til {v.open_until}</div>}
                  </div>
                  {v.tonight_event && (
                    <div className="mt-3 flex items-center gap-1.5 rounded-xl border border-neon-violet/30 bg-neon-violet/5 px-3 py-2 text-[11px] font-bold text-neon-violet" data-testid={`venue-tonight-${v.id}`}>
                      <Fire size={12} weight="fill" /> Tonight: {v.tonight_event.title}
                    </div>
                  )}
                </div>
              </motion.button>
            ))}
          </div>
        )}

        {venues !== null && venues.length > 0 && (
          <div className="mt-12 rounded-3xl border border-neon-gold/25 bg-neon-gold/5 p-8 text-center">
            <p className="font-display text-xl font-black">Own a venue?</p>
            <p className="mx-auto mt-2 max-w-md text-sm text-zinc-400">
              Founding venues lock in a lifetime rate, get the door scanner, promoter payouts and verified attendance.
            </p>
            <Link to="/auth?mode=register" data-testid="directory-founder-cta"
              className="btn-neon mt-5 inline-flex !px-6 !py-2.5 text-sm">Claim a Founders Spot</Link>
          </div>
        )}
      </div>
    </div>
  );
}
